import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { ActiveStatusBadge } from '../../components/ui/ActiveStatusBadge';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { MarkdownGuidanceDetails } from '../../components/ui/MarkdownGuidanceDetails';
import { MarkdownGuidanceField } from '../../components/ui/MarkdownGuidanceField';
import { SortOrderControls } from '../../components/ui/SortOrderControls';
import { useMessageBox } from '../../contexts/MessageBoxContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { useSortOrderReorder } from '../../hooks/useSortOrderReorder';
import {
  Competency,
  CompetencyAudience,
  CompetencyUpsertPayload,
  PerformanceIndicator,
  createCompetency,
  deleteCompetency,
  listCompetencies,
  listPerformanceIndicators,
  updateCompetency,
} from '../../services/peoplePerformanceApi';
import { nextSortOrder } from '../../utils/sortOrderList';

type CompetencyFormState = Omit<CompetencyUpsertPayload, 'sortOrder'>;

const AUDIENCE_OPTIONS: { value: CompetencyAudience; label: string }[] = [
  { value: 'Colaborador', label: 'Colaborador' },
  { value: 'Gestor', label: 'Gestor' },
  { value: 'Executivo', label: 'Executivo' },
];

const emptyForm: CompetencyFormState = {
  indicatorId: '',
  name: '',
  description: '',
  guidanceMarkdown: '',
  isActive: true,
};

function audienceLabel(value?: string | null): string {
  return AUDIENCE_OPTIONS.find((option) => option.value === value)?.label ?? value ?? '—';
}

function buildCompetencyPayload(item: Competency, sortOrder: number): CompetencyUpsertPayload {
  return {
    indicatorId: item.indicatorId,
    name: item.name,
    description: item.description,
    guidanceMarkdown: item.guidanceMarkdown,
    sortOrder,
    isActive: item.isActive,
  };
}

export function CompetenciesPage() {
  const { confirmDelete } = useMessageBox();
  const { canWriteModule } = usePermissions();
  const canWrite = canWriteModule('people.competencies');
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } = useCollapsibleItemForm();
  const [items, setItems] = useState<Competency[]>([]);
  const [indicators, setIndicators] = useState<PerformanceIndicator[]>([]);
  const [form, setForm] = useState<CompetencyFormState>(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [indicatorFilter, setIndicatorFilter] = useState('');
  const [audienceFilter, setAudienceFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const selectedIndicator = useMemo(
    () => indicators.find((indicator) => indicator.id === form.indicatorId) ?? null,
    [form.indicatorId, indicators],
  );

  async function loadIndicators(): Promise<void> {
    try {
      const result = await listPerformanceIndicators({ pageSize: 100, activeOnly: true });
      setIndicators(result.items);
    } catch {
      toast.error('Falha ao carregar indicadores.');
    }
  }

  async function loadData(): Promise<void> {
    setLoading(true);
    try {
      const result = await listCompetencies({
        pageSize: 100,
        indicatorId: indicatorFilter || undefined,
        audience: audienceFilter || undefined,
      });
      setItems(result.items);
    } catch {
      toast.error('Falha ao carregar competências.');
    } finally {
      setLoading(false);
    }
  }

  const { sortedItems, moving, move } = useSortOrderReorder({
    items,
    canReorder: canWrite,
    updateItem: async (item, sortOrder) => {
      await updateCompetency(item.id, buildCompetencyPayload(item, sortOrder));
    },
    onReordered: () => loadData(),
  });

  useEffect(() => {
    void loadIndicators();
  }, []);

  useEffect(() => {
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [indicatorFilter, audienceFilter]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function startEdit(item: Competency): void {
    expandForm();
    setEditingId(item.id);
    setForm({
      indicatorId: item.indicatorId,
      name: item.name,
      description: item.description,
      guidanceMarkdown: item.guidanceMarkdown ?? '',
      isActive: item.isActive,
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const editingItem = editingId ? items.find((item) => item.id === editingId) : null;
    const payload: CompetencyUpsertPayload = {
      ...form,
      sortOrder: editingItem?.sortOrder ?? nextSortOrder(items),
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateCompetency(editingId, payload);
        toast.success('Competência atualizada.');
        resetForm();
      } else {
        await createCompetency(payload);
        toast.success('Competência criada.');
        handleCreateSuccess(resetForm);
      }
      await loadData();
    } catch {
      toast.error('Falha ao salvar competência.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string): Promise<void> {
    if (!canWrite) return;
    const item = items.find((current) => current.id === id);
    if (
      !(await confirmDelete({
        title: 'Excluir competência',
        message: item ? `Excluir "${item.name}"?` : 'Excluir esta competência?',
      }))
    ) {
      return;
    }
    try {
      await deleteCompetency(id);
      toast.success('Competência excluída.');
      await loadData();
    } catch {
      toast.error('Falha ao excluir competência.');
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Competências"
        description="Catálogo de competências vinculadas aos indicadores de desempenho."
        actions={(
          <div className="flex flex-wrap gap-2">
            <Link to="/people/performance-indicators" className="erp-btn-secondary text-sm">
              Indicadores
            </Link>
            <Link to="/people/competency-questions" className="erp-btn-secondary text-sm">
              Perguntas
            </Link>
          </div>
        )}
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar competência' : 'Nova competência'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          isEditing={!!editingId}
          createSummary="Clique para expandir e cadastrar uma nova competência"
        >
          <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3">
            <div className="erp-form-grid">
              <label className="erp-label">
                Indicador
                <select
                  className="erp-input"
                  value={form.indicatorId}
                  required
                  onChange={(event) => setForm({ ...form, indicatorId: event.target.value })}
                >
                  <option value="">Selecione...</option>
                  {indicators.map((indicator) => (
                    <option key={indicator.id} value={indicator.id}>
                      {indicator.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="erp-label">
                Nome
                <input className="erp-input" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />
              </label>
            </div>
            {selectedIndicator ? (
              <p className="mt-3 text-sm text-secondary-600">
                Público-alvo (herdado do indicador):{' '}
                <span className="font-medium text-secondary-900">{audienceLabel(selectedIndicator.audience)}</span>
              </p>
            ) : null}
            <label className="erp-label mt-3 block">
              Descrição
              <textarea className="erp-input mt-1" value={form.description ?? ''} onChange={(event) => setForm({ ...form, description: event.target.value })} />
            </label>
            <div className="mt-3">
              <MarkdownGuidanceField
                value={form.guidanceMarkdown ?? ''}
                onChange={(guidanceMarkdown) => setForm({ ...form, guidanceMarkdown })}
              />
            </div>
            <label className="erp-label-inline mt-3">
              <input type="checkbox" checked={form.isActive ?? true} onChange={(event) => setForm({ ...form, isActive: event.target.checked })} />
              Ativa
            </label>
            <FormActionBar onCancel={() => collapseForm(resetForm)} className="erp-btn-actions mt-4">
              <button type="submit" disabled={saving} className="erp-btn-primary">
                {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
              </button>
            </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm font-medium text-secondary-800">Listagem</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <select
            className="erp-input-compact max-w-xs"
            value={indicatorFilter}
            onChange={(event) => setIndicatorFilter(event.target.value)}
            aria-label="Filtrar por indicador"
          >
            <option value="">Todos os indicadores</option>
            {indicators.map((indicator) => (
              <option key={indicator.id} value={indicator.id}>
                {indicator.name}
              </option>
            ))}
          </select>
          <select
            className="erp-input-compact max-w-xs"
            value={audienceFilter}
            onChange={(event) => setAudienceFilter(event.target.value)}
            aria-label="Filtrar por público-alvo"
          >
            <option value="">Todos os públicos</option>
            {AUDIENCE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-secondary-500">Carregando...</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-secondary-200 bg-white">
          <table className="erp-table">
            <thead>
              <tr>
                {canWrite ? <th className="w-10" /> : null}
                <th>Nome</th>
                <th>Indicador</th>
                <th>Público</th>
                <th>Status</th>
                <th className="w-24">Ações</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.length === 0 ? (
                <tr>
                  <td colSpan={canWrite ? 6 : 5} className="text-center text-secondary-500">
                    Nenhuma competência cadastrada.
                  </td>
                </tr>
              ) : (
                sortedItems.map((item, index) => (
                  <tr key={item.id}>
                    {canWrite ? (
                      <td>
                        <SortOrderControls
                          canMoveUp={index > 0}
                          canMoveDown={index < sortedItems.length - 1}
                          disabled={moving}
                          onMoveUp={() => void move(index, 'up')}
                          onMoveDown={() => void move(index, 'down')}
                        />
                      </td>
                    ) : null}
                    <td>
                      <div className="font-medium text-secondary-900">{item.name}</div>
                      <MarkdownGuidanceDetails value={item.guidanceMarkdown} />
                    </td>
                    <td>{item.indicatorName}</td>
                    <td>{audienceLabel(item.audience)}</td>
                    <td>
                      <ActiveStatusBadge active={item.isActive} feminine />
                    </td>
                    <td>
                      {canWrite ? (
                        <div className="flex gap-1">
                          <EditIconButton label="Editar competência" onClick={() => startEdit(item)} />
                          <DeleteIconButton label="Excluir competência" onClick={() => void handleDelete(item.id)} />
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </PageShell>
  );
}
